import React from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import TruncatedText from '../truncatedText/TruncatedText'

// Antd
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FlagFilled } from '@ant-design/icons'
import { selectTableFilter } from './store/selectors'
import { AnsvarigHandläggareEntry, ansvarigHandläggareColumn } from './ansvarigHandläggareColumn'
import { HanteringEntry, hanteringColumn } from './hanteringColumn'


export const getColor = (risk: number) => {
  switch (risk) {
    case 1:
      return 'yellow'
    case 2:
      return 'orange'
    case 3:
      return 'red'
    case 4:
      return 'black'
    default:
      return 'green'
  }
}

const formatDateShort = (date: Date | string) => {
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const formatDateMonth = (date: Date | string) => {
  return moment(date).format('MMM-YY')
}

export interface DataType {
  id?: number
  key: React.Key
  flagga: number
  flaggnamn: string
  type?: number
  nivå: number
  namn: string
  // Personens id i Pejlen på fliken Personal. `id` är flaggans (#99)
  medarbetare_id?: number
  personnummer?: string
  från: Date
  till: Date
  created: Date
  utförare: {
    utförare_id: number
    utförare_namn: string
  }[]
  ansvarig_handläggare?: AnsvarigHandläggareEntry[]
  hantering?: HanteringEntry
}

// Filtreringen sker i Filter-popovern (single.tsx, #37) — kolumnerna har bara sortering kvar.
// Sort-state ligger i tableFilterReducer, nycklat per `stateKey` (flik + utförare) så varje
// utförare har egen sortering. `path` skiljer brukare/personal (länk + personnummer-kolumn).
export const CreateFilterConfig = (path: string, stateKey: string = path, dateFrom?: string, dateTo?: string) => {
  const filterState = useSelector(selectTableFilter)
  const sortOrderFor = (key: string) => {
    const sorter = filterState?.[stateKey]?.sorter
    return sorter?.columnKey === key && sorter?.order ? sorter.order : null
  }
  const columns: ColumnsType<DataType> = [
    {
      title: 'Namn',
      dataIndex: 'name',
      key: 'name',
      width: 190,
      fixed: 'left',
      sortOrder: sortOrderFor('name'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: path === 'brukare' ? 'Brukarens namn' : 'Personalens namn' },
      render: (_, coworker) => (
        <TruncatedText
          text={coworker.namn}
          to={{
            pathname: path === 'brukare' ? `/app/brukare/id=${coworker.personnummer}&namn=${coworker.namn}` : `/app/medarbetare/${coworker.medarbetare_id}`,
            state: { from: dateFrom, to: dateTo },
          }}
        />
      ),
      sorter: (a, b) => a.namn.localeCompare(b.namn),
    },
    {
      title: 'Flagga',
      dataIndex: 'deviation',
      key: 'deviation',
      width: 220,
      sortOrder: sortOrderFor('deviation'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Typ av flagga' },
      sorter: (a, b) => a.flaggnamn.localeCompare(b.flaggnamn),
      render: (_, obj) => (
        <TruncatedText
          text={obj.flaggnamn}
          to={{
            pathname: `/app/avvikelse/flag=${obj.flagga}&id=${obj.key}`,
            state: { flagname: obj.flaggnamn, riskniva: obj.nivå },
          }}
          linkId="flagLink"
        />
      ),
    },
    {
      title: 'Risknivå',
      dataIndex: 'risk',
      key: 'risk',
      width: 120,
      sortOrder: sortOrderFor('risk'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Flaggans risknivå, dvs. dess allvarlighetsgrad' },
      sorter: (a, b) => a.nivå - b.nivå,
      render: (_, obj) => (
        <>
          {obj.nivå} <FlagFilled style={{ color: getColor(obj.nivå) }} />
        </>
      ),
    },
    {
      title: 'Skapad',
      dataIndex: 'created',
      key: 'created',
      width: 95,
      sortOrder: sortOrderFor('created'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Datum för den körning då flaggan genererades' },
      sorter: (a, b) => {
        return new Date(a.created).getTime() - new Date(b.created).getTime()
      },
      render: (_, obj) => {
        return <>{formatDateMonth(obj.created)}</>
      },
    },
    {
      title: 'Period',
      dataIndex: 'periodStart',
      key: 'periodStart',
      width: 200,
      sortOrder: sortOrderFor('periodStart'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Tidsperiod som flaggan baserades på' },
      sorter: (a, b) => {
        const from = new Date(a.från)
        const to = new Date(b.från)
        return from.getTime() - to.getTime()
      },
      render: (_, obj) => <>{formatDateShort(obj.från)} - {formatDateShort(obj.till)}</>,
    },
    Table.SELECTION_COLUMN,
  ]

  const personnummerColumn: ColumnsType<DataType> = [
    {
      title: 'Personnummer',
      dataIndex: 'personnummer',
      key: 'personnummer',
      width: 160,
      sortOrder: sortOrderFor('personnummer'),
      showSorterTooltip: { overlayClassName: 'flag-tooltip', title: 'Brukarens personnummer' },
      render: (_, coworker) => coworker.personnummer && <TruncatedText text={coworker.personnummer} />,
      sorter: (a, b) => a.personnummer!.localeCompare(b.personnummer!),
    },
  ]

  // Brukare-data har personnummer → lägg in kolumnen direkt efter Namn.
  if (path === 'brukare') {
    columns.splice(1, 0, personnummerColumn[0])
  }

  // Ansvarig handläggare gäller bara brukare (personal har ingen handläggare). Placeras före
  // Skapad, samma relativa plats som i brukare-listan.
  if (path === 'brukare') {
    const skapadIndex = columns.findIndex((c) => (c as { key?: unknown }).key === 'created')
    columns.splice(skapadIndex >= 0 ? skapadIndex : columns.length - 1, 0, ansvarigHandläggareColumn({ sortOrder: sortOrderFor('ansvarig_handläggare') }))
  }

  // Status gäller alla flaggtyper — läget betyder samma sak oavsett vad flaggan riktar
  // sig mot. Läggs sist före markeringskolumnen och är fast till höger, se kolumnfilen.
  const selectionIndex = columns.findIndex((c) => c === Table.SELECTION_COLUMN)
  columns.splice(selectionIndex >= 0 ? selectionIndex : columns.length, 0, hanteringColumn({ sortOrder: sortOrderFor('hantering') }))

  return columns
}
